<script setup lang="ts">
import { computed, onMounted, reactive, watch, ref } from 'vue';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { useToast } from 'primevue/usetoast';
import Calendar from 'primevue/calendar';
import { PersonStores, useDisciplineStore } from '@/store';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';
import { TreatmentPlanBaseInterface, TreatmentPlanCategoryInterface } from '@/types';

const toast = useToast();
const treatmentPlansStore = PersonStores.useUserTreatmentPlansStore();
const profileStore = PersonStores.useUserProfileStore();
const disciplineStore = useDisciplineStore();
const referredDiscipline = ref({});

interface GeneralInterface {
  id?: string;
  start_at?: string;
  person_id?: string;
  treatment_plan_type?: TreatmentPlanBaseInterface;
  treatment_plan_category?: TreatmentPlanCategoryInterface;
}

let general: GeneralInterface = reactive({
  id: undefined,
  start_at: undefined,
  treatment_plan_type: undefined,
  treatment_plan_category: undefined,
  person_id: undefined,
});

const rules = {
  start_at: { required },
  treatment_plan_type: { required },
  treatment_plan_category: { required },
};

const setTreatmentInfo = async () => {
  disciplineStore.getDisciplines();
  if (treatmentPlansStore.userTreatmentPlans) {
    const { id, start_at, treatment_plan_type, treatment_plan_category, person_id } =
      treatmentPlansStore.userTreatmentPlans;
    general.id = id;
    general.start_at = start_at;
    general.treatment_plan_type = treatment_plan_type;
    general.treatment_plan_category = treatment_plan_category;
    general.person_id = person_id;
  }
};

onMounted(() => setTreatmentInfo());

watch(() => treatmentPlansStore.userTreatmentPlans, setTreatmentInfo);

const v$ = useVuelidate(rules, general);

const calendarStartDate = computed({
  get: () => {
    if (general.start_at) {
      let segments: (string | number)[] = general.start_at.split('-');
      segments[1] = (segments[1] as number) - 1;
      return new Date(...segments);
    } else {
      return null;
    }
  },
  // eslint-disable-next-line
  set: (value: any) => {
    const d = new Date(value).toISOString();
    const newDate = d.substring(0, 10);
    if (newDate != general.start_at) {
      v$.value.start_at.$touch();
    }
    general.start_at = newDate;
  },
});

const isLoading = computed(() => treatmentPlansStore.loading);

const cloneTreatmentPlan = () => {
  return { ...treatmentPlansStore.userTreatmentPlans };
};

const onCancel = () => {
  if (treatmentPlansStore.userTreatmentPlans?.person_id) {
    const { start_at, treatment_plan_type, treatment_plan_category } = cloneTreatmentPlan();
    general.start_at = start_at as string;
    general.treatment_plan_type = treatment_plan_type;
    general.treatment_plan_category = treatment_plan_category;
    treatmentPlansStore.getTreatmentPlanCategories();
  } else {
    general.treatment_plan_category = undefined;
    general.treatment_plan_type = undefined;
    general.start_at = '';
  }
  v$.value.$reset();
};

const onPlanTypeChange = () => {
  treatmentPlansStore.getTreatmentPlanCategories(general.treatment_plan_type.id);
  general.treatment_plan_category = undefined;
};

const doSuccess = (message: string) => {
  toast.add({
    severity: 'success',
    summary: 'Saved',
    detail: message,
    life: 3000,
  });
};

const doError = (message: string) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: message,
    life: 3000,
  });
};

const updateGeneralTreatmentPlan = async () => {
  await treatmentPlansStore
    .updateGeneralTreatmentPlan()
    .then(() => {
      doSuccess('Treatment Plan has been saved');
      v$.value.$reset();
    })
    .catch((response) => {
      treatmentPlansStore.userTreatmentPlans = treatmentPlansStore.clonedUserTreatmentPlans;
      onCancel();
      treatmentPlansStore.error = undefined;
      doError(response.statusText);
    });
};

const addNewTreatmentPlan = async () => {
  await treatmentPlansStore
    .addNewTreatmentPlan()
    .then(() => {
      doSuccess('Treatment Plan has been created');
      v$.value.$reset();
    })
    .catch((response) => {
      treatmentPlansStore.userTreatmentPlans = treatmentPlansStore.clonedUserTreatmentPlans;
      onCancel();
      treatmentPlansStore.error = undefined;
      doError(response.statusText);
    });
};

const onSave = async () => {
  v$.value.$touch();

  if (!v$.value.$invalid) {
    const generalBackup = cloneTreatmentPlan();

    treatmentPlansStore.userTreatmentPlans = generalBackup;
    treatmentPlansStore.userTreatmentPlans.start_at = general.start_at as string;
    treatmentPlansStore.userTreatmentPlans.treatment_plan_type =
      general.treatment_plan_type as TreatmentPlanBaseInterface;
    treatmentPlansStore.userTreatmentPlans.treatment_plan_category =
      general.treatment_plan_category as TreatmentPlanCategoryInterface;

    if (treatmentPlansStore.userTreatmentPlans?.person_id) {
      await updateGeneralTreatmentPlan();
    } else {
      await addNewTreatmentPlan();
    }
  }
  return;
};

watch(
  () => profileStore.person,
  (newVal) => {
    referredDiscipline.value = newVal?.referred_discipline?.[0]?.discipline_id;
  },
  { immediate: true },
);

const referrerDisciplineChange = () => {
  profileStore.updateReferredDiscipline(referredDiscipline.value ? [referredDiscipline.value] : []);
};
</script>

<template>
  <FormCard title="General" class="card-full-width" :dirty="v$.$anyDirty" :cancel="onCancel" :save="onSave">
    <template #form>
      <div class="formgrid grid">
        <div class="field col flex flex-column" :class="{ error: v$.treatment_plan_type.$errors.length }">
          <label for="treatmentTypes">Treatment Plan Type</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <Dropdown
            v-if="!isLoading"
            id="treatmentTypes"
            v-model="general.treatment_plan_type"
            :options="treatmentPlansStore.treatmentPlanTypes"
            option-label="name"
            placeholder="Select a Treatment Plan Type"
            :class="{ 'p-invalid': v$.treatment_plan_type.$invalid && v$.treatment_plan_type.$dirty }"
            :disabled="!can([PERMISSIONS.PERSON_TREATMENT_PLANS_UPDATE_STUDENTS])"
            @change="
              v$.treatment_plan_type.$touch();
              onPlanTypeChange();
            "
          />
          <div v-for="error of v$.treatment_plan_type.$errors" :key="error.$uid" class="input-errors">
            <div class="error-msg">{{ error.$message }}</div>
          </div>
        </div>
        <div class="field col flex flex-column" :class="{ error: v$.treatment_plan_category.$errors.length }">
          <label for="treatmentCategory">Treatment Category</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <Dropdown
            v-if="!isLoading"
            id="treatmentCategory"
            v-model="general.treatment_plan_category"
            :options="treatmentPlansStore.treatmentPlanCategories"
            option-label="name"
            placeholder="Select a Category"
            :class="{ 'p-invalid': v$.treatment_plan_category.$invalid && v$.treatment_plan_category.$dirty }"
            :disabled="!can([PERMISSIONS.PERSON_TREATMENT_PLANS_UPDATE_STUDENTS])"
            @change="v$.treatment_plan_category.$touch"
          />
          <div v-for="error of v$.treatment_plan_category.$errors" :key="error.$uid" class="input-errors">
            <div class="error-msg">{{ error.$message }}</div>
          </div>
        </div>
        <div class="field col flex flex-column" :class="{ error: v$.start_at.$errors.length }">
          <label for="treatmentStartDate">Treatment Start Date</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <Calendar
            v-if="!isLoading"
            v-model="calendarStartDate"
            placeholder="Select a Treatment Start Date"
            date-format="mm/dd/yy"
            :disabled="!can([PERMISSIONS.PERSON_TREATMENT_PLANS_UPDATE_STUDENTS])"
            :class="{ 'p-invalid': v$.start_at.$invalid && v$.start_at.$dirty }"
          />
          <div v-for="error of v$.start_at.$errors" :key="error.$uid" class="input-errors">
            <div class="error-msg">{{ error.$message }}</div>
          </div>
        </div>
      </div>
      <div class="formgrid grid">
        <div class="field col flex flex-column">
          <label for="treatmentTypes">Referred Discipline</label>
          <Skeleton v-if="disciplineStore.loading" height="2.3rem" />
          <Dropdown
            v-if="!disciplineStore.loading"
            v-model="referredDiscipline"
            :options="disciplineStore.disciplines"
            option-label="name"
            option-value="id"
            placeholder="Select a discipline"
            @change="referrerDisciplineChange()"
          />
        </div>
      </div>
    </template>
  </FormCard>
</template>
